<template>
    <div>
        <h1>Indicator observations</h1>

        <div v-if="loading" class="alert alert-info">
            Loading indicator list..
        </div>

        <div v-if="listIndicators.length">
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <button class="btn btn-dark" type="button" v-on:click="stepBackward" :disabled="!canGoBackward">
                        <font-awesome-icon icon="step-backward" />
                    </button>
                </div>
                <select class="form-control indicator-list" :value="activeListIndicatorIndex" @input="proxyActiveListIndicatorIndex = $event.target.value">
                    <option value="">Select an indicator from the list</option>
                    <option
                            v-for="(listIndicator, indicatorIndex) in listIndicators"
                            :key="listIndicator.id"
                            v-text="getListIndicatorText(listIndicator)"
                            :value="indicatorIndex"
                    />
                </select>
                <div class="input-group-append">
                    <button class="btn btn-dark" type="button" v-on:click="stepForward" :disabled="!canGoForward">
                        <font-awesome-icon icon="step-forward" />
                    </button>
                </div>
            </div>

            <div class="alert alert-warning animated" :class="changeAlertAnimate ? 'shake-new' : ''" v-if="showChangeAlert">
                Do you want to save changes to the current indicator before leaving?<br /><br />
                <button
                        class="btn btn-success mr-3"
                        v-on:click="save"
                        :class="!canSave || saving ? 'disabled' : ''"
                        :disabled="!canSave || saving">
                    Save
                </button>
                <button
                        class="btn btn-primary mr-3"
                        v-on:click="clearChanges"
                        :class="!canClearChanges ? 'disabled' : ''"
                        :disabled="!canClearChanges">
                    Don't save
                </button>
                <button
                        class="btn btn-primary"
                        v-on:click="alertCancel">
                    Cancel
                </button>
            </div>
            <div v-else-if="activeIndicator" class="row mb-3">
                <div class="col-sm-6 text-left">
                    <button
                            v-if="canClearChanges"
                            class="btn btn-danger"
                            v-on:click="clearChanges"
                            :class="!canClearChanges ? 'disabled' : ''"
                            :disabled="!canClearChanges"
                    >Clear unsaved changes</button>
                </div>
                <div class="col-sm-6 text-right">
                    <button
                            class="btn btn-success"
                            v-on:click="save"
                            :class="!canSave || saving ? 'disabled' : ''"
                            :disabled="!canSave || saving"
                            v-text="saving ? 'Saving draft..' : 'Save draft'"
                    />

                    <button
                            class="btn btn-info ml-3"
                            v-on:click="publish"
                            :class="!canPublish || publishing ? 'disabled' : ''"
                            :disabled="!canPublish || publishing"
                            v-text="publishing ? 'Publishing..' : 'Publish'"
                            :title="hasChanges ? 'You cannot publish if there are unsaved changes' : ''"
                    />
                </div>
            </div>

            <div class="alert alert-dark" v-if="loadingIndicator">
                Loading indicator data..
            </div>
        </div>

        <div v-if="activeIndicator" class="well active-indicator" :class="isSuccess ? 'is-success' : 'is-error'">
            <div class="alert alert-warning" v-if="isDraft">
                <strong>NOTE:</strong> This indicator is currently in draft state. In order to publish, first make sure that there are no invalid values, then save the changes, and finally press the "Publish" button.<br />
                <br/>
                <strong>Last publish time:</strong> {{ activeIndicator.updateTime }}<br />
                <br />
                <button
                        class="btn btn-danger"
                        v-on:click="revertDraft"
                        :class="!canRevertDraft ? 'disabled' : ''"
                        :disabled="!canRevertDraft"
                        v-text="revertingDraft ? 'Reverting draft changes..' : 'Revert all draft changes'"
                ></button>
            </div>

            <div>
                <div>
                    <strong>Indicator:</strong> {{ activeIndicator.indicator }}
                </div>
                <div v-if="!realIndicator" style="font-style: italic">
                    <strong>Note:</strong> This is a special non-indicator for all observations not belonging to an actual indicator
                </div>
            </div>
            <div>
                <div>
                    <strong>Observation groups</strong>
                </div>

                <div>
                    <observation-group
                            class="observation-group"

                            :indicatorId="activeIndicator.id"

                            v-for="(observationGroupId, i) in observationGroupIds"
                            v-on:delete="onObservationGroupDelete(observationGroupId)"
                            v-on:moveUp="onObservationGroupMoveUp(i, observationGroupId)"
                            v-on:moveDown="onObservationGroupMoveDown(i, observationGroupId)"

                            :index="i"
                            :key="observationGroupId"
                            :observationGroup="observationGroups[observationGroupId]"
                            :originIndicator="activeIndicator"
                            :maxIndex="observationGroupIds.length - 1"
                    />
                </div>
                <div>
                    <button class="btn btn-primary" v-on:click="addObservationGroup">Add observation group
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import fastDeepEqual from "fast-deep-equal";
    import cloneDeep from "clone-deep";

    import Vue from 'vue';
    import ObservationGroup from "@/components/admin/observation-group/ObservationGroup";
    import {createId} from "@/utils/idCreator";
    import {validateIndicator} from "@/utils/admin/indicators/ExceptionValidator";
    import { mapGetters } from 'vuex'
    import * as types from '@/store/mutation-types'
    import {objectKeyCount, objectKeys, hasUndefinedKey} from '@/utils/objectUtils';
    import {trim} from '@/utils/stringUtils';

    export default {
        name: 'indicators-index',
        components: {
            ObservationGroup
        },
        mounted() {
            this.loading = true;

            this.$apiClient.getIndicators().then((listIndicators) => {
                this.loading = false;

                this.lastIndicatorIndex = listIndicators.length - 1;

                Vue.set(this, 'listIndicators', listIndicators);
            })
        },
        data() {
            return {
                saving: false,
                canSave: false,

                published: false,
                publishing: false,
                canPublish: false,

                revertingDraft: false,

                loading: false,
                loadingIndicator: false,

                listIndicators: [],
                proxyActiveListIndicatorIndex: '',
                activeListIndicatorIndex: '',
                confirmListIndicatorIndex: '',

                observationGroupIds: [],
                observationGroups: {},

                activeIndicator: null,

                hasChanges: false,

                initialObservationGroupData: null,
                initialIndicatorData: null,
                showChangeAlert: false,
                changeAlertAnimate: false,

                lastIndicatorIndex: null,
            }
        },
        watch: {
            proxyActiveListIndicatorIndex(newValue, oldValue) {
                if (this.showChangeAlert) {
                    this.confirmListIndicatorIndex = newValue;

                    if (newValue === this.activeListIndicatorIndex) {
                        this.showChangeAlert = false;
                    } else {
                        this.alertAttention();
                    }

                    return false;
                }

                if (this.hasChanges) {
                    this.showChangeAlert = true;

                    this.alertAttention();

                    this.confirmListIndicatorIndex = newValue;
                } else {
                    this.activeListIndicatorIndex = newValue;
                }
            },
            activeListIndicatorIndex(activeListIndicatorIndex) {
                if (activeListIndicatorIndex === '') {
                    return;
                }

                this.loadActiveIndicator();
            },
            activeIndicatorData: {
                // This will let Vue know to look inside the array
                deep: true,

                // We have to move our method to a handler field
                handler(newValue) {
                    if (this.initialObservationGroupData) {
                        this.hasChanges = !fastDeepEqual(this.initialObservationGroupData, newValue.observationGroups);
                    }

                    this.canPublish = !this.published && !this.hasChanges && this.validate();
                    this.canSave = this.hasChanges;
                }
            },
        },
        computed: {
            ...mapGetters({
                activeIndicatorData: 'getActiveIndicatorData',
            }),
            isSuccess() {
                return this.validate();
            },
            realIndicator() {
                if (!this.activeIndicatorData) {
                    return true;
                }

                return this.activeIndicatorData.category !== 'NONE';
            },
            canGoBackward() {
                if (this.showChangeAlert) {
                    return false;
                }

                if (this.activeListIndicatorIndex === '') {
                    return false;
                }

                return this.activeListIndicatorIndex > 0;
            },
            canGoForward() {
                if (this.showChangeAlert) {
                    return false;
                }

                if (this.activeListIndicatorIndex === '') {
                    return true;
                }

                return this.activeListIndicatorIndex < this.lastIndicatorIndex;
            },
            canRevertDraft() {
                if (this.canClearChanges) {
                    return false;
                }

                if (!this.isDraft) {
                    return false;
                }

                if (this.revertingDraft) {
                    return false;
                }

                return true;
            },
            canClearChanges() {
                return this.hasChanges;
            },
            isDraft() {
                return this.activeIndicator && this.activeIndicator.draftDetails;
            }
        },
        methods: {
            validate(scanChildren = true) {
                if (!this.activeIndicatorData) {
                    return false;
                }

                try {
                    let result = validateIndicator(this.activeIndicatorData, scanChildren);

                    result = typeof(result) !== 'undefined' ? result : true;

                    console.log('validateIndicatorSuccess', result);

                    return result;
                } catch (error) {
                    console.log('validateIndicatorError', error.message || error.toString());

                    return false;
                }
            },
            getListIndicatorText(listIndicator) {
                let icon = '〰️';

                if (listIndicator.draftDetails) {
                    icon = '📝'; //
                } else if (listIndicator.isEmpty) {
                    icon = '⚫️️️️';
                } else {
                    icon = listIndicator.isValid ? '✔️' : '❌️';
                }

                let indicatorText = icon + ' ' + listIndicator.rowNumber + ' | ' + listIndicator.indicator;

                return trim(indicatorText, 100);
            },
            stepBackward() {
                if (!this.canGoBackward) {
                    return;
                }

                this.proxyActiveListIndicatorIndex = (parseInt(this.proxyActiveListIndicatorIndex, 10) - 1).toString();
            },
            stepForward() {
                if (!this.canGoForward) {
                    return;
                }

                if (!this.proxyActiveListIndicatorIndex) {
                    this.proxyActiveListIndicatorIndex = '0';
                } else {
                    this.proxyActiveListIndicatorIndex = (parseInt(this.proxyActiveListIndicatorIndex, 10) + 1).toString();
                }
            },
            loadActiveIndicator() {
                let activeListIndicatorIndex = this.activeListIndicatorIndex;

                this.activeIndicator = null;
                this.hasChanges = false;
                this.showChangeAlert = false;
                this.changeAlertAnimate = false;

                this.loadingIndicator = true;

                this.$apiClient.getIndicator(this.listIndicators[activeListIndicatorIndex].id).then((activeIndicator) => {
                    this.loadingIndicator = false;

                    Vue.set(this, 'activeIndicator', activeIndicator);

                    let observationGroups = activeIndicator.observationGroups;

                    if (observationGroups instanceof Array) {
                        observationGroups = {};
                    }

                    let indicatorData = {
                        age: activeIndicator.age,
                        category: activeIndicator.category,
                        gender: activeIndicator.gender,
                        hasObservationGroups: activeIndicator.hasObservationGroups,
                        id: activeIndicator.id,
                        indicator: activeIndicator.indicator,
                        line: activeIndicator.line,
                        relativity: activeIndicator.relativity,
                        specificity: activeIndicator.specificity,
                        subCategory: activeIndicator.subCategory,
                        observationGroups: observationGroups
                    };

                    this.$store.commit(types.SET_ACTIVE_INDICATOR_DATA, indicatorData)

                    this.observationGroupIds = objectKeys(observationGroups);
                    this.observationGroups = observationGroups;

                    this.initialIndicatorData = cloneDeep(indicatorData);
                    this.initialObservationGroupData = null;
                    this.hasChanges = false;
                    this.published = !activeIndicator.draftDetails;

                    this.$nextTick(() => {
                        this.initialObservationGroupData = cloneDeep(this.observationGroups);
                    });
                });
            },
            alertAttention() {
                this.changeAlertAnimate = false;

                this.$nextTick(() => {
                    this.changeAlertAnimate = true;
                })
            },
            clearChanges() {
                if (this.proxyActiveListIndicatorIndex !== this.activeListIndicatorIndex) {
                    this.activeListIndicatorIndex = this.proxyActiveListIndicatorIndex;
                } else {
                    this.loadActiveIndicator();
                }
            },
            revertDraft() {
                if (!this.canRevertDraft) {
                    return false;
                }

                if (!confirm('Are you sure you want to revert all draft changes? This will restore the indicator to the last time the indicator was published.')) {
                    return false;
                }

                this.revertingDraft = true;

                this.$apiClient.revertIndicatorDraftChanges(this.activeIndicator.id).then(() => {
                    this.revertingDraft = false;

                    this.$notify({
                        message: `Indicator ${this.activeIndicator.id} successfully reverted`,
                        type: 'success'
                    });

                    this.$nextTick(() => {
                        this.showChangeAlert = false;

                        Vue.set(this.listIndicators[this.activeListIndicatorIndex], 'draftDetails', null);
                        Vue.set(this.activeIndicator, 'draftDetails', null);

                        this.publishing = false;
                        this.canPublish = false;

                        if (this.confirmListIndicatorIndex) {
                            this.activeListIndicatorIndex = this.confirmListIndicatorIndex;
                            this.confirmListIndicatorIndex = '';
                        }

                        this.published = true;

                        this.loadActiveIndicator();
                    });
                }, (error) => {
                    this.revertingDraft = false;

                    this.$notify({
                        message: `Could not revert indicator: ${error}`,
                        type: 'danger'
                    });
                });
            },
            alertCancel() {
                this.proxyActiveListIndicatorIndex = this.activeListIndicatorIndex;
            },
            addObservationGroup() {
                let data = {
                    id: createId()
                };

                Vue.set(this.observationGroups, data.id, data)

                this.$store.commit(types.SET_ACTIVE_INDICATOR_OBSERVATION_GROUP_DATA, {
                    observationGroupId: data.id,
                    observationGroupData: {
                        id: data.id,
                        observations: {}
                    }
                });

                this.observationGroupIds.push(data.id);

                this.hasChanges = true;
            },
            swapObservationGroups(index1, index2) {
                let observationGroup1 = this.observationGroupIds[index1];
                let observationGroup2 = this.observationGroupIds[index2];

                Vue.set(this.observationGroupIds, index1, observationGroup2);
                Vue.set(this.observationGroupIds, index2, observationGroup1);
            },
            onObservationGroupMoveUp(index, id) {
                // Move up!
                this.swapObservationGroups(index, index - 1);
            },
            onObservationGroupMoveDown(index, id) {
                // Move down!
                this.swapObservationGroups(index, index + 1);
            },
            onObservationGroupDelete(id) {
                this.observationGroupIds.splice(this.observationGroupIds.indexOf(id), 1);

                Vue.delete(this.observationGroups, id);

                this.$nextTick(() => {
                    this.$store.commit(types.REMOVE_ACTIVE_INDICATOR_OBSERVATION_GROUP_DATA, {
                        observationGroupId: id
                    });
                })
            },
            publish() {
                if (!this.canPublish) {
                    return;
                }

                if (this.published) {
                    return;
                }

                if (this.publishing) {
                    return;
                }

                this.publishing = true;

                this.$apiClient.publishIndicator(this.activeIndicator.id).then(() => {
                    this.showChangeAlert = false;

                    this.$notify({
                        message: `Indicator ${this.activeIndicator.id} successfully published`,
                        type: 'success'
                    });

                    Vue.set(this.listIndicators[this.activeListIndicatorIndex], 'draftDetails', null);
                    Vue.set(this.activeIndicator, 'draftDetails', null);

                    this.publishing = false;
                    this.canPublish = false;

                    if (this.confirmListIndicatorIndex) {
                        this.activeListIndicatorIndex = this.confirmListIndicatorIndex;
                        this.confirmListIndicatorIndex = '';
                    }

                    this.published = true;

                    this.$nextTick(() => {
                        this.initialObservationGroupData = cloneDeep(this.observationGroups);
                    });
                }, (error) => {
                    this.publishing = false;

                    this.$notify({
                        message: `Could not publish indicator: ${error}`,
                        type: 'danger'
                    });
                })
            },
            save() {
                if (!this.canSave) {
                    return;
                }

                if (this.saving) {
                    return;
                }

                this.saving = true;

                this.$apiClient.saveIndicator(this.activeIndicator.id, this.activeIndicatorData).then((response) => {
                    this.showChangeAlert = false;

                    this.$notify({
                        message: `Indicator ${this.activeIndicator.id} successfully saved`,
                        type: 'success'
                    });

                    console.log(response);

                    Vue.set(this.listIndicators[this.activeListIndicatorIndex], 'draftDetails', response.draftDetails);
                    Vue.set(this.activeIndicator, 'draftDetails', response.draftDetails);

                    this.saving = false;

                    this.hasChanges = false;
                    this.canSave = false;
                    this.published = false;

                    if (this.confirmListIndicatorIndex) {
                        this.activeListIndicatorIndex = this.confirmListIndicatorIndex;
                        this.confirmListIndicatorIndex = '';
                    }

                    this.canPublish = !this.published && !this.hasChanges && this.validate();

                    this.$nextTick(() => {
                        this.initialObservationGroupData = cloneDeep(this.observationGroups);
                    });
                }, (error) => {
                    this.saving = false;

                    this.$notify({
                        message: `Could not save indicator: ${error}`,
                        type: 'danger'
                    });
                })
            },
        }
    }
</script>

<style scoped lang="scss">
    .well {
        background: #e9ecef;
        padding: 10px;
        border-radius: 0 0 10px 10px;
    }

    .active-indicator {
        &.is-error {
            border: 1px solid #ff3333;
            box-shadow: 2px 2px 2px 0px rgba(0, 0, 0, .5);
            background: #ff5555;
        }
    }

    .indicator-list {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    }

    $distance: 6px;

    @-webkit-keyframes shake-new {
        from,
        to {
            -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
        }
        20%,
        60% {
            -webkit-transform: translate3d(-$distance, 0, 0);
            transform: translate3d(-$distance, 0, 0);
        }

        40%,
        80% {
            -webkit-transform: translate3d($distance, 0, 0);
            transform: translate3d($distance, 0, 0);
        }
    }

    @keyframes shake-new {
        from,
        to {
            -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
        }

        20%,
        60% {
            -webkit-transform: translate3d(-$distance, 0, 0);
            transform: translate3d(-$distance, 0, 0);
        }

        40%,
        80% {
            -webkit-transform: translate3d($distance, 0, 0);
            transform: translate3d($distance, 0, 0);
        }
    }

    .shake-new {
        -webkit-animation-name: shake-new;
        animation-name: shake-new;

        --animate-duration: .5s !important;
        --animate-delay: 0 !important;
        animation-duration: .5s !important;
        animation-delay: 0 !important;
    }
</style>
